import { useState } from 'react';
import { Bell, Shield, AlertTriangle, XCircle, Info, X, CheckCheck } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';

const severityConfig = {
  critical: {
    icon: XCircle,
    color: 'text-red-500',
    bg: 'bg-red-50 dark:bg-red-950',
    badge: 'destructive'
  },
  error: {
    icon: AlertTriangle,
    color: 'text-orange-500',
    bg: 'bg-orange-50 dark:bg-orange-950',
    badge: 'destructive'
  },
  warning: {
    icon: AlertTriangle,
    color: 'text-yellow-500',
    bg: 'bg-yellow-50 dark:bg-yellow-950',
    badge: 'warning'
  },
  info: {
    icon: Info,
    color: 'text-blue-500',
    bg: 'bg-blue-50 dark:bg-blue-950',
    badge: 'secondary'
  }
};

const alertTypeLabels = {
  login_failed: 'Login Fallito',
  brute_force_detected: 'Attacco Brute Force',
  rate_limit_exceeded: 'Rate Limit Superato',
  suspicious_ip: 'IP Sospetto',
  sql_injection_attempt: 'Tentativo SQL Injection',
  xss_attempt: 'Tentativo XSS',
  unauthorized_access: 'Accesso Non Autorizzato',
  multiple_failed_logins: 'Login Falliti Multipli',
  account_lockout: 'Account Bloccato'
};

function formatTimestamp(timestamp) {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  
  if (diffMins < 1) return 'Adesso';
  if (diffMins < 60) return `${diffMins}m fa`;
  if (diffHours < 24) return `${diffHours}h fa`;
  return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function SecurityAlertItem({ alert, onMarkAsRead }) {
  const config = severityConfig[alert.severity] || severityConfig.info;
  const Icon = config.icon;
  
  return (
    <div 
      className={`p-3 border-b last:border-b-0 cursor-pointer transition-colors hover:bg-muted/50 ${!alert.read ? config.bg : ''}`}
      onClick={() => onMarkAsRead(alert.id)}
    >
      <div className="flex items-start gap-3">
        <Icon className={`h-5 w-5 mt-0.5 ${config.color}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className={`text-sm font-medium ${!alert.read ? 'text-foreground' : 'text-muted-foreground'}`}>
              {alertTypeLabels[alert.alert_type] || alert.alert_type}
            </span>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {formatTimestamp(alert.timestamp)}
            </span>
          </div>
          <p className={`text-xs mt-1 line-clamp-2 ${!alert.read ? 'text-foreground' : 'text-muted-foreground'}`}>
            {alert.message}
          </p>
          {alert.details?.ip_address && (
            <span className="text-xs text-muted-foreground mt-1 block">
              IP: {alert.details.ip_address}
            </span>
          )}
        </div>
        {!alert.read && (
          <div className="w-2 h-2 rounded-full bg-primary mt-2" />
        )}
      </div>
    </div>
  );
}

export function SecurityAlertsDropdown({ alerts, unreadCount, connected, onMarkAsRead, onMarkAllAsRead, onClear }) {
  const [open, setOpen] = useState(false);
  
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Shield className={`h-5 w-5 ${connected ? 'text-green-500' : 'text-muted-foreground'}`} />
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-[10px]"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            <span>Alert di Sicurezza</span>
            {connected && (
              <span className="flex items-center gap-1 text-xs text-green-500">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                Live
              </span>
            )}
          </div>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-6 text-xs"
              onClick={(e) => {
                e.stopPropagation();
                onMarkAllAsRead();
              }}
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              Leggi tutti
            </Button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {alerts.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            <Shield className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Nessun alert</p>
            <p className="text-xs">Il sistema è protetto</p>
          </div>
        ) : (
          <>
            <ScrollArea className="h-[300px]">
              {alerts.map((alert) => (
                <SecurityAlertItem 
                  key={alert.id} 
                  alert={alert} 
                  onMarkAsRead={onMarkAsRead}
                />
              ))}
            </ScrollArea>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              className="justify-center text-muted-foreground"
              onClick={onClear}
            >
              <X className="h-4 w-4 mr-1" />
              Cancella tutti
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default SecurityAlertsDropdown;
